// Pure, testable filtering + session-generation logic for the Question Helper
// setup page, plus localStorage helpers for the fixed session and saved filters.
import { getDeviceId } from './deviceId';

export const SESSION_KEY = 'qh_active_session_v1';
export const PREFS_KEY = 'qh_filter_preferences_v1';

export const PAPERS = [
  { code: 'all', name: 'All papers' },
  { code: 'P1', name: 'Paper 1: Physical Geography' },
  { code: 'P2', name: 'Paper 2: Human Geography' },
  { code: 'P4', name: 'Paper 4: Geographical Investigations' },
];

const hasCaseStudy = (q) => Boolean(q.case_study && String(q.case_study).trim());

export function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const uniqSorted = (vals) => [...new Set(vals)];

// Distinct option universe for the bank, used for defaults.
export function bankUniverse(questions) {
  const active = questions.filter((q) => q.active);
  return {
    topics: uniqSorted(active.map((q) => q.topic_name)).sort(),
    marks: uniqSorted(active.map((q) => q.marks)).sort((a, b) => a - b),
    commandTerms: uniqSorted(active.map((q) => q.command_term)).sort(),
    difficulties: uniqSorted(active.map((q) => q.difficulty)),
  };
}

// Topics for a paper ('all' => grouped by paper).
export function topicsForPaper(questions, paper) {
  const active = questions.filter(
    (q) => q.active && (paper === 'all' || q.paper_code === paper)
  );
  const groups = {};
  active.forEach((q) => {
    if (!groups[q.paper_code]) groups[q.paper_code] = { paper_code: q.paper_code, paper_name: q.paper_name, topics: new Set() };
    groups[q.paper_code].topics.add(q.topic_name);
  });
  return Object.values(groups)
    .sort((a, b) => a.paper_code.localeCompare(b.paper_code))
    .map((g) => ({ ...g, topics: [...g.topics].sort() }));
}

// Available marks / command terms / difficulties given upstream selections.
export function availableMarks(questions, paper, topics) {
  return uniqSorted(
    questions
      .filter((q) => q.active && (paper === 'all' || q.paper_code === paper) && topics.includes(q.topic_name))
      .map((q) => q.marks)
  ).sort((a, b) => a - b);
}

export function availableCommandTerms(questions, paper, topics, marks) {
  return uniqSorted(
    questions
      .filter(
        (q) =>
          q.active &&
          (paper === 'all' || q.paper_code === paper) &&
          topics.includes(q.topic_name) &&
          marks.includes(q.marks)
      )
      .map((q) => q.command_term)
  ).sort();
}

export function availableDifficulties(questions, paper, topics, marks, commandTerms) {
  return uniqSorted(
    questions
      .filter(
        (q) =>
          q.active &&
          (paper === 'all' || q.paper_code === paper) &&
          topics.includes(q.topic_name) &&
          marks.includes(q.marks) &&
          commandTerms.includes(q.command_term)
      )
      .map((q) => q.difficulty)
  );
}

// Progress status using the anonymous qh_progress summary records.
export function progressStatusFor(question, progressMap) {
  const p = progressMap && progressMap[question.question_id];
  if (!p || !p.attempt_count) return 'unseen';
  if (p.setup_mastered) return 'mastered';
  return 'needs_practice';
}

// Inclusive multi-select filters: a question matches only if its value is in the
// selected array. Empty multi-select arrays therefore match nothing (Start gets
// disabled), which is the intended "Clear selection" behaviour.
export function filterQuestions(questions, f, progressMap) {
  return questions.filter((q) => {
    if (!q.active) return false;
    if (f.paper && f.paper !== 'all' && q.paper_code !== f.paper) return false;
    if (!f.topics.includes(q.topic_name)) return false;
    if (!f.marks.includes(q.marks)) return false;
    if (!f.commandTerms.includes(q.command_term)) return false;
    if (!f.difficulties.includes(q.difficulty)) return false;
    if (f.questionType === 'case_study' && !hasCaseStudy(q)) return false;
    if (f.questionType === 'non_case_study' && hasCaseStudy(q)) return false;
    if (f.progress && f.progress !== 'all') {
      if (progressStatusFor(q, progressMap) !== f.progress) return false;
    }
    return true;
  });
}

export function summarise(matched, selectedTopicsCount) {
  const byPaper = {};
  const marks = new Set();
  matched.forEach((q) => {
    byPaper[q.paper_code] = (byPaper[q.paper_code] || 0) + 1;
    marks.add(q.marks);
  });
  return {
    total: matched.length,
    byPaper,
    marks: [...marks].sort((a, b) => a - b),
    topicsSelected: selectedTopicsCount,
  };
}

// Distribute low/middle/full as evenly as possible, then randomise.
export function assignBalancedBands(n) {
  const bands = ['low', 'middle', 'full'];
  const out = [];
  for (let i = 0; i < n; i += 1) out.push(bands[i % 3]);
  return shuffle(out);
}

function orderQuestions(sampled, order) {
  if (order === 'group_by_topic') {
    const byTopic = {};
    sampled.forEach((q) => { (byTopic[q.topic_name] = byTopic[q.topic_name] || []).push(q); });
    return Object.keys(byTopic).sort().flatMap((t) => shuffle(byTopic[t]));
  }
  if (order === 'marks_asc') return [...sampled].sort((a, b) => a.marks - b.marks);
  if (order === 'marks_desc') return [...sampled].sort((a, b) => b.marks - a.marks);
  return shuffle(sampled); // random
}

// Build a fixed session object from matched questions and chosen options.
export function buildSession(matched, filters) {
  const requested = filters.length === 'all' ? matched.length : Math.min(filters.length, matched.length);
  // Sample without replacement (random) then apply ordering.
  const sampled = shuffle(matched).slice(0, requested);
  const ordered = orderQuestions(sampled, filters.order);

  const bands = assignBalancedBands(ordered.length);
  const selected_answer_ids = {};
  ordered.forEach((q, i) => {
    const want = bands[i];
    const byBand = q.model_answers.find((m) => m.quality_band === want);
    const chosen = byBand || q.model_answers[Math.floor(Math.random() * q.model_answers.length)];
    selected_answer_ids[q.question_id] = chosen.answer_id;
  });

  return {
    session_id: (crypto.randomUUID && crypto.randomUUID()) || `${Date.now()}-${Math.random()}`,
    anonymous_device_id: getDeviceId(),
    created_at: new Date().toISOString(),
    current_question_index: 0,
    status: 'active',
    filters,
    question_ids: ordered.map((q) => q.question_id),
    selected_answer_ids,
    question_order: filters.order,
    completed_question_ids: [],
  };
}

// localStorage helpers (fail-safe)
function readJSON(key) {
  try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : null; } catch (e) { return null; }
}
function writeJSON(key, val) {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch (e) { /* ignore */ }
}

export const getActiveSession = () => readJSON(SESSION_KEY);
export const saveActiveSession = (s) => writeJSON(SESSION_KEY, s);
export const clearActiveSession = () => { try { localStorage.removeItem(SESSION_KEY); } catch (e) { /* ignore */ } };
export function updateActiveSession(patch) {
  const s = getActiveSession();
  if (!s) return null;
  const next = { ...s, ...patch };
  saveActiveSession(next);
  return next;
}

export const getFilterPrefs = () => readJSON(PREFS_KEY);
export const saveFilterPrefs = (f) => writeJSON(PREFS_KEY, f);
